import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, Button, TextField } from '@mui/material';
import type { TextFieldProps } from '@mui/material/TextField';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

type DatePickerTextFieldSlotProps = Partial<TextFieldProps> & {
  'data-testid'?: string;
};

import clsx from 'clsx';
import { DateTime } from 'luxon';
import type React from 'react';
import { useMemo, useState } from 'react';
import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';
import { DEFAULT_DATE_FORMAT } from '~/common/constants/common.constant';
import { useDayPattern } from '~/common/localization/useDayPattern';
import useStyles from './CSDateRangeFilter.styles';

/**
 * Reads back one of the strings this component emits.
 *
 * `new Date('08/09/2026')` takes the month first whatever shape the string is in, so a reader who
 * writes dates day-first would have every date they picked silently shifted by months. Reading
 * against the same pattern the value was written with is the only reading that cannot drift.
 *
 * @param value - A date in `datePattern`, or an empty string for no date.
 * @param datePattern - The pattern the value was written with.
 * @returns The date, or `null` if there is none to read.
 */
const readDate = (value: string, datePattern: string): DateTime | null => {
  const parsed = DateTime.fromFormat(value, datePattern);

  return parsed.isValid ? parsed : null;
};

export interface CSDateRangeFilterProps {
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  startDateProps?: Partial<TextFieldProps>;
  endDateProps?: Partial<TextFieldProps>;
  dateFormat?: string;
}

/**
 * CSDateRangeFilter
 *
 * Reusable date range picker for BrightDrop fleet management apps.
 * Supports session-based state isolation and context-aware configuration.
 *
 * Props:
 * - startDate: string
 *     Selected start date (formatted as a string).
 * - endDate: string
 *     Selected end date (formatted as a string).
 * - onStartDateChange: (date: string) => void
 *     Callback fired when the start date changes.
 * - onEndDateChange: (date: string) => void
 *     Callback fired when the end date changes.
 * - startDateProps?: Partial<TextFieldProps>
 *     Additional props for the start date input field.
 *     Use to override MUI TextField props (e.g., className, inputProps, placeholder).
 * - endDateProps?: Partial<TextFieldProps>
 *     Additional props for the end date input field.
 *     Use to override MUI TextField props (e.g., className, inputProps, placeholder).
 * - dateFormat?: string
 *     The pattern the two strings are written and read in. What this component exchanges with its
 *     caller, and not what the fields show: the fields follow the reader, through
 *     `useDayPattern()`. The two are separable because the fields are read-only — a day is picked
 *     from the calendar and never typed, so nothing the reader sees is read back as input.
 *     A caller that persists what it is given leaves this alone, because a stored day has to mean
 *     the same date to whoever opens it next. One that keeps its days as `yyyy-MM-dd` says so and
 *     hands them straight over.
 *
 * Placeholder Customization:
 * - To change the start/end date field placeholders, pass a `placeholder` property in
 *   `startDateProps` or `endDateProps`.
 *   Example:
 *     <CSDateRangeFilter
 *       startDate={...}
 *       endDate={...}
 *       onStartDateChange={...}
 *       onEndDateChange={...}
 *       startDateProps={{ placeholder: "Select start date" }}
 *       endDateProps={{ placeholder: "Select end date" }}
 *     />
 * - If no placeholder is provided, the default localized label is used (`common:startDate` or `common:endDate`).
 *
 * File location: apps/fleet-management/src/common/components/CSDateRangeFilter/CSDateRangeFilter.tsx
 */
const CSDateRangeFilter: React.FC<CSDateRangeFilterProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  startDateProps = {},
  endDateProps = {},
  dateFormat = DEFAULT_DATE_FORMAT,
}) => {
  const { classes } = useStyles();
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);

  /* What the reader is shown. `dateFormat` stays the caller's, so whoever picks a day here, the
   * caller is handed it back in the shape it stores. */
  const displayPattern = useDayPattern();

  const today = useMemo(() => DateTime.now(), []);
  const oneYearAgo = today.minus({ years: 1 });

  const start = readDate(startDate, dateFormat);
  const end = readDate(endDate, dateFormat);

  const handleStartDateChange = (value: unknown) => {
    const date = value as DateTime | null;
    if (date) {
      onStartDateChange(date.toFormat(dateFormat));
      if (end && end < date) {
        onEndDateChange('');
      }
    }
    setStartDateOpen(false);
  };

  const handleEndDateChange = (value: unknown) => {
    const date = value as DateTime | null;
    if (date) {
      onEndDateChange(date.toFormat(dateFormat));
    }
    setEndDateOpen(false);
  };

  const TodayButton = ({ isStartDate }: { isStartDate: boolean }) => (
    <Box sx={{ padding: 1, display: 'flex', justifyContent: 'flex-end' }}>
      <Button
        onClick={() => {
          const todayFormatted = today.toFormat(dateFormat);
          if (isStartDate) {
            onStartDateChange(todayFormatted);
            if (end && end < today) {
              onEndDateChange('');
            }
            setStartDateOpen(false);
          } else {
            onEndDateChange(todayFormatted);
            setEndDateOpen(false);
          }
        }}
        variant="text"
        size="small"
      >
        {translateText('common:dateRange.today')}
      </Button>
    </Box>
  );

  const startTextFieldProps: DatePickerTextFieldSlotProps = {
    variant: 'filled',
    className: clsx(classes.customDateField, startDateProps.className),
    onClick: () => setStartDateOpen(true),
    'data-testid': 'cs-date-range-filter-start-date',
    ...startDateProps,
    inputProps: {
      ...startDateProps.inputProps,
      readOnly: true,
      placeholder: startDateProps?.placeholder
        ? (startDateProps.placeholder as string)
        : translateText('common:startDate'),
    },
    InputProps: {
      ...startDateProps.InputProps,
      startAdornment: (
        <CalendarTodayOutlinedIcon className={classes.calendarIcon} />
      ),
      endAdornment: <KeyboardArrowDownIcon className={classes.expandIcon} />,
    },
  };

  const endTextFieldProps: DatePickerTextFieldSlotProps = {
    variant: 'filled',
    className: clsx(classes.customDateField, endDateProps.className),
    onClick: () => setEndDateOpen(true),
    'data-testid': 'cs-date-range-filter-end-date',
    ...endDateProps,
    inputProps: {
      ...endDateProps.inputProps,
      readOnly: true,
      placeholder: endDateProps?.placeholder
        ? (endDateProps.placeholder as string)
        : translateText('common:endDate'),
    },
    InputProps: {
      ...endDateProps.InputProps,
      startAdornment: (
        <CalendarTodayOutlinedIcon className={classes.calendarIcon} />
      ),
      endAdornment: <KeyboardArrowDownIcon className={classes.expandIcon} />,
    },
  };

  return (
    <Box className={classes.customDateContainer}>
      <DatePicker
        format={displayPattern}
        open={startDateOpen}
        onClose={() => setStartDateOpen(false)}
        value={start}
        onChange={handleStartDateChange}
        maxDate={today}
        minDate={oneYearAgo}
        slots={{
          textField: TextField,
          actionBar: () => <TodayButton isStartDate={true} />,
        }}
        slotProps={{
          popper: {
            className: classes.datePickerPopper,
          },
          textField: startTextFieldProps,
        }}
      />

      <DatePicker
        format={displayPattern}
        open={endDateOpen}
        onClose={() => setEndDateOpen(false)}
        value={end}
        onChange={handleEndDateChange}
        maxDate={today}
        minDate={start ?? oneYearAgo}
        slots={{
          textField: TextField,
          actionBar: () => <TodayButton isStartDate={false} />,
        }}
        slotProps={{
          popper: {
            className: classes.datePickerPopper,
          },
          textField: endTextFieldProps,
        }}
      />
    </Box>
  );
};

export default CSDateRangeFilter;
